import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const headerRef = useRef(null);

  const navLinks = [
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'The Real World', href: '#real-world' },
    { label: 'What You Get', href: '#what-you-get' },
    { label: "Who It's For", href: '#who-this-is-for' },
    { label: 'Founder', href: '#founder' },
    { label: 'FAQ', href: '#faq' },
  ];

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const smoothScrollTo = (e, href) => {
    e.preventDefault();
    setMobileMenuOpen(false);

    if (href === '#top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const targetElement = document.querySelector(href);
    if (targetElement) {
      const isMobile = window.innerWidth < 768;
      // On mobile, offset by navbar height + half a screen before the section
      const mobileExtraOffset = isMobile ? window.innerHeight * 0.5 : 0;
      const headerOffset = 72 + mobileExtraOffset;
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = Math.max(0, elementPosition + window.pageYOffset - headerOffset);

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <header ref={headerRef} className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#E5E2DA] transition-all">
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 lg:px-8 h-[64px] sm:h-[68px] flex items-center justify-between">
        {/* Logo */}
        <a 
          href="#top" 
          onClick={(e) => smoothScrollTo(e, '#top')}
          className="flex items-center hover:opacity-90 transition-opacity py-1 select-none"
        >
          <img 
            src="/logo.svg" 
            alt="Sparklyt" 
            className="h-7 sm:h-8 md:h-9 w-auto object-contain" 
          />
        </a>

        {/* Desktop Nav Links */}
        <nav className="hidden md:flex items-center gap-7 text-[15px] font-medium text-[#635F58]">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => smoothScrollTo(e, link.href)}
              className="hover:text-[#1A1917] transition-colors cursor-pointer py-1"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-4">
          <a
            href="#start"
            onClick={(e) => smoothScrollTo(e, '#start')}
            className="spark-btn cursor-pointer"
          >
            Start free
            <ArrowRight size={15} />
          </a>
        </div>

        {/* Animated Mobile menu button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-[5px] p-2 text-[#1A1917] hover:bg-[#FAF9F6] active:bg-[#F4F2EC] rounded-[2px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C07A22]"
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
        >
          <span 
            className={`w-5 h-[2px] bg-[#1A1917] rounded-full transition-all duration-300 ease-in-out transform origin-center ${
              mobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''
            }`}
          />
          <span 
            className={`w-5 h-[2px] bg-[#1A1917] rounded-full transition-all duration-300 ease-in-out ${
              mobileMenuOpen ? 'opacity-0 scale-x-0' : 'opacity-100 scale-x-100'
            }`}
          />
          <span 
            className={`w-5 h-[2px] bg-[#1A1917] rounded-full transition-all duration-300 ease-in-out transform origin-center ${
              mobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''
            }`}
          />
        </button>
      </div>

      {/* Smooth Animated Mobile dropdown menu */}
      <div 
        className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out border-b bg-[#FAF9F6]/98 backdrop-blur-lg ${
          mobileMenuOpen 
            ? 'max-h-[460px] opacity-100 border-[#E5E2DA] shadow-xl py-4' 
            : 'max-h-0 opacity-0 border-transparent py-0 pointer-events-none'
        }`}
      >
        <div className="px-4 sm:px-6 space-y-4">
          <nav className="flex flex-col space-y-1 text-[15.5px] font-medium text-[#635F58]">
            {navLinks.map((link, idx) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => smoothScrollTo(e, link.href)}
                className={`py-2.5 px-3 rounded-[2px] transition-all duration-200 hover:text-[#1A1917] hover:bg-[#EFECE3] active:bg-[#E5E1D5] cursor-pointer flex items-center justify-between ${
                  mobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
                }`}
                style={{ 
                  transitionProperty: 'opacity, transform, background-color, color',
                  transitionDuration: '250ms',
                  transitionDelay: mobileMenuOpen ? `${idx * 35}ms` : '0ms' 
                }}
              >
                <span>{link.label}</span>
                <ArrowRight size={14} className="text-[#8E8A82]" />
              </a>
            ))}
          </nav>
          <div className="pt-3 border-t border-[#E5E2DA]">
            <a
              href="#start"
              onClick={(e) => smoothScrollTo(e, '#start')}
              className="spark-btn w-full justify-center cursor-pointer py-3 text-[15.5px]"
            >
              Start free
              <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
